import { useTranslation } from 'react-i18next';
import { describeWhen } from './lib/defectTypes';

/**
 * Returns a formatter for defect timestamps — "07:03 today", "Yesterday",
 * "Yesterday, 18:10", or an absolute date for anything older.
 *
 * This lives in a hook rather than taking `t` as a parameter because the
 * typed-keys i18n augmentation only accepts static string-literal keys at the
 * call site; a `t` narrowed to `(key: string) => string` fails to type-check.
 * Same constraint that drives the pre-computed label maps elsewhere in the app.
 *
 * `withTime` controls whether a same-day-or-older stamp carries its clock
 * time: queue cards want the short form, the action history wants the long one.
 */
export function useWhenLabel(): (iso: string, withTime: boolean) => string {
  const { t } = useTranslation('checks');

  return (iso, withTime) => {
    const { kind, time, date } = describeWhen(iso);
    if (kind === 'today') return t('defects.time.today', { time });
    if (kind === 'yesterday') {
      return withTime ? t('defects.time.yesterdayAt', { time }) : t('defects.time.yesterday');
    }
    return withTime ? t('defects.time.olderAt', { date, time }) : date;
  };
}
